import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Receipt,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Paperclip,
  X,
  Save,
  Info,
  FileText,
  Route,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/toaster.jsx';
import { cn } from '@/lib/utils';
import { fetchChargeConceptsByModo } from '../../../parametrizacion/charge-concepts/Services/chargeConceptsService.js';
import { listLclCotizaciones } from '../../cotizaciones/lclCotizaciones.service.js';
import ConceptoCombobox from '@/components/ConceptoCombobox';
import {
  listarRecargos,
  crearRecargo,
  actualizarRecargo,
  eliminarRecargo,
  subirSoporte,
  urlSoporte,
} from '../lclOperaciones.service.js';

const MONEDAS = ['USD', 'COP', 'EUR', 'MXN'];
const MAX_MB = 20;

const selectCls =
  'flex h-9 rounded-md border border-input bg-background px-2 text-sm ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const importe = (n, moneda) => {
  if (n === null || n === undefined || n === '') return '—';
  const dec = moneda === 'COP' ? 0 : 2;
  return `${moneda} ${Number(n).toLocaleString('es-CO', {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  })}`;
};

/** dd/mm/aaaa de una fecha sin hora: en UTC, o se corre un día en Colombia. */
const fecha = (v) => {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  const p = (x) => String(x).padStart(2, '0');
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
};

const hoy = () => {
  const d = new Date();
  const p = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const vacio = (proveedor = '') => ({
  _id: null,
  fecha: hoy(),
  conceptoCodigo: null,
  concepto: '',
  descripcion: '',
  proveedorNombre: proveedor,
  costoMonto: '',
  costoMoneda: 'USD',
  trasladarCliente: true,
  cobroMonto: '',
  cobroMoneda: 'USD',
  soporte: null,
  // Publicación en la trazabilidad (sólo al registrar).
  publicar: false,
  textoCliente: '',
  textoEditado: false,
  avisarCliente: false,
});

/**
 * Texto por defecto del hito, igual al que arma el backend. La
 * trazabilidad la ve el CLIENTE: va el concepto, la descripción y lo que
 * se le cobra. Nunca el costo del proveedor ni quién lo cobra.
 */
function textoParaCliente(f) {
  let t = String(f.concepto || '').trim();
  if (!t) return '';
  if (f.descripcion?.trim()) t += `: ${f.descripcion.trim()}`;
  if (!/[.!?]$/.test(t)) t += '.';
  if (f.trasladarCliente && f.cobroMonto !== '' && f.cobroMonto != null) {
    const dec = f.cobroMoneda === 'COP' ? 0 : 2;
    const valor = Number(f.cobroMonto).toLocaleString('es-CO', {
      minimumFractionDigits: dec,
      maximumFractionDigits: dec,
    });
    t += ` Valor a cobrar: ${f.cobroMoneda} ${valor}.`;
  }
  return t;
}

/** Mismo cálculo que el backend, para la vista previa mientras se edita. */
function margenDe(f) {
  const costo = Number(f.costoMonto);
  if (f.costoMonto === '' || !Number.isFinite(costo)) return null;
  if (!f.trasladarCliente) return -costo;
  if (f.cobroMonto === '' || f.cobroMoneda !== f.costoMoneda) return null;
  return Number(f.cobroMonto) - costo;
}

/**
 * Recargos que aparecen durante una operación LCL en curso (bodegaje,
 * demora, manipulación extra…).
 *
 * Se registra lo que cobra el proveedor y, por separado, si se traslada
 * al cliente y por cuánto. La utilidad se calcula sólo cuando costo y
 * cobro están en la misma moneda: no se inventa una tasa de cambio.
 */
export default function RecargosOperacionDialog({ open, onOpenChange, lclRequest }) {
  const toast = useToast();
  const id = lclRequest?._id;

  const [datos, setDatos] = useState({ recargos: [], resumen: null });
  const [cargando, setCargando] = useState(true);
  const [catalogo, setCatalogo] = useState([]);
  const [proveedorSugerido, setProveedorSugerido] = useState('');
  const [form, setForm] = useState(null);
  const [archivo, setArchivo] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [aBorrar, setABorrar] = useState(null);
  const inputArchivo = useRef(null);

  const cargar = useCallback(async () => {
    if (!id) return;
    setCargando(true);
    try {
      const r = await listarRecargos(id);
      if (r?.success) setDatos(r.data);
      else toast.error('No se pudieron cargar los recargos', r?.message);
    } finally {
      setCargando(false);
    }
  }, [id, toast]);

  useEffect(() => {
    if (!open) return;
    setForm(null);
    cargar();
    fetchChargeConceptsByModo('lcl')
      .then((r) => r?.success && setCatalogo(r.data || []))
      .catch(() => {});
    // El proveedor de la cotización elegida es quien suele cobrar el
    // recargo: se sugiere, pero se puede cambiar.
    listLclCotizaciones(id)
      .then((r) => {
        const sel = (r?.data || []).find((c) => c.estado === 'Seleccionada');
        setProveedorSugerido(sel?.proveedorSnapshot?.nombre || '');
      })
      .catch(() => {});
  }, [open, id, cargar]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const nuevo = () => {
    setArchivo(null);
    setForm(vacio(proveedorSugerido));
  };

  const editar = (r) => {
    setArchivo(null);
    setForm({
      ...vacio(),
      ...r,
      fecha: r.fecha ? new Date(r.fecha).toISOString().slice(0, 10) : hoy(),
      costoMonto: r.costoMonto ?? '',
      cobroMonto: r.cobroMonto ?? '',
      cobroMoneda: r.cobroMoneda || r.costoMoneda,
    });
  };

  const elegirArchivo = (f) => {
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error('Archivo muy grande', `El máximo es ${MAX_MB} MB`);
      return;
    }
    setArchivo(f);
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      let soporte = form.soporte || null;
      if (archivo) soporte = await subirSoporte(id, archivo);

      const payload = {
        fecha: form.fecha,
        conceptoCodigo: form.conceptoCodigo || null,
        concepto: form.concepto.trim(),
        descripcion: form.descripcion || '',
        proveedorNombre: form.proveedorNombre || '',
        costoMonto: Number(form.costoMonto),
        costoMoneda: form.costoMoneda,
        trasladarCliente: !!form.trasladarCliente,
        cobroMonto: form.trasladarCliente ? Number(form.cobroMonto) : null,
        cobroMoneda: form.cobroMoneda,
        soporte: soporte
          ? {
              r2Key: soporte.r2Key,
              nombreOriginal: soporte.nombreOriginal,
              mimeType: soporte.mimeType,
              tamanoBytes: soporte.tamanoBytes,
            }
          : null,
      };
      if (!form._id && form.publicar) {
        payload.trazabilidad = {
          registrar: true,
          texto: form.textoEditado ? form.textoCliente : textoParaCliente(form),
          notificar: !!form.avisarCliente,
        };
      }
      const r = form._id
        ? await actualizarRecargo(form._id, payload)
        : await crearRecargo(id, payload);
      if (r?.success) {
        const traza = r.data?.trazabilidad;
        // El backend responde "Recargo registrado, publicado en la …": el
        // título ya lo dice, en la descripción va sólo lo demás.
        const resto = String(r.message || '')
          .replace(/^Recargo (registrado|actualizado)[,.]?\s*/i, '')
          .trim();
        const detalle = resto ? resto[0].toUpperCase() + resto.slice(1) : payload.concepto;
        if (traza && !traza.registrado) {
          // El recargo se guardó; lo que falló fue publicarlo.
          toast.error('Recargo registrado, sin publicar', detalle);
        } else {
          toast.success(form._id ? 'Recargo actualizado' : 'Recargo registrado', detalle);
        }
        setForm(null);
        setArchivo(null);
        cargar();
      } else {
        toast.error('No se pudo guardar', [].concat(r?.message || '').join(' · '));
      }
    } catch (e) {
      toast.error('No se pudo guardar', e?.message);
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async () => {
    const r = await eliminarRecargo(aBorrar._id);
    if (r?.success) {
      toast.success('Recargo eliminado', aBorrar.concepto);
      if (form?._id === aBorrar._id) setForm(null);
      cargar();
    } else {
      toast.error('No se pudo eliminar', r?.message);
    }
    setABorrar(null);
  };

  const verSoporte = async (r) => {
    const x = await urlSoporte(r._id);
    if (x?.success && x.data?.url) window.open(x.data.url, '_blank', 'noopener');
    else toast.error('No se pudo abrir el soporte', x?.message);
  };

  const margenForm = form ? margenDe(form) : null;
  const formValido =
    form &&
    form.concepto.trim() &&
    form.costoMonto !== '' &&
    Number(form.costoMonto) >= 0 &&
    (!form.trasladarCliente || (form.cobroMonto !== '' && Number(form.cobroMonto) >= 0));

  const rs = datos.resumen;
  const monedasResumen = useMemo(
    () =>
      rs
        ? [...new Set([...Object.keys(rs.costoPorMoneda), ...Object.keys(rs.cobroPorMoneda)])]
        : [],
    [rs],
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !guardando && onOpenChange?.(v)}>
      <DialogContent
        hideClose
        className="flex h-[calc(100vh-2rem)] w-[95vw] max-w-5xl flex-col gap-0 overflow-hidden p-0"
      >
        {/* Encabezado */}
        <div className="flex shrink-0 items-start justify-between gap-3 border-b p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-rose-100 p-2 text-rose-700">
              <Receipt className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-lg">Recargos de la operación</DialogTitle>
              <DialogDescription className="text-sm">
                {lclRequest?.codigo} · {lclRequest?.clienteSnapshot?.nombre || '—'}
              </DialogDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!form && (
              <Button size="sm" onClick={nuevo}>
                <Plus className="mr-1.5 h-4 w-4" /> Registrar recargo
              </Button>
            )}
            <Button variant="ghost" size="icon" aria-label="Cerrar" onClick={() => onOpenChange?.(false)}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Resumen */}
        {rs && rs.cantidad > 0 && (
          <div className="flex shrink-0 flex-wrap gap-x-6 gap-y-2 border-b bg-muted/30 px-4 py-3 text-sm">
            {monedasResumen.map((m) => (
              // Cada moneda muestra sólo lo que tiene: si en COP sólo hay
              // cobro, no se pinta un "Costo COP 0" que confunde.
              <div key={m} className="flex flex-wrap gap-x-4">
                {rs.costoPorMoneda[m] != null && (
                  <span>
                    <span className="text-muted-foreground">Costo </span>
                    <span className="font-medium">{importe(rs.costoPorMoneda[m], m)}</span>
                  </span>
                )}
                {rs.cobroPorMoneda[m] != null && (
                  <span>
                    <span className="text-muted-foreground">Cobro al cliente </span>
                    <span className="font-medium">{importe(rs.cobroPorMoneda[m], m)}</span>
                  </span>
                )}
                {rs.margenPorMoneda[m] != null && (
                  <span>
                    <span className="text-muted-foreground">Utilidad </span>
                    <span
                      className={cn(
                        'font-semibold',
                        rs.margenPorMoneda[m] < 0 ? 'text-rose-700' : 'text-emerald-700',
                      )}
                    >
                      {importe(rs.margenPorMoneda[m], m)}
                    </span>
                  </span>
                )}
              </div>
            ))}
            {rs.recargosSinMargen > 0 && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Info className="h-3 w-3" />
                {rs.recargosSinMargen} cobrado(s) en otra moneda: su utilidad no entra al total.
              </span>
            )}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {/* Formulario */}
          {form && (
            <div className="mb-4 space-y-3 rounded-lg border bg-background p-4 shadow-sm">
              <p className="text-sm font-semibold">
                {form._id ? 'Editar recargo' : 'Registrar recargo'}
              </p>
              <div className="grid gap-3 md:grid-cols-4">
                <div className="space-y-1">
                  <Label htmlFor="rc-fecha" className="text-xs text-muted-foreground">Fecha</Label>
                  <Input id="rc-fecha" type="date" value={form.fecha} onChange={(e) => set('fecha', e.target.value)} />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label htmlFor="rc-concepto" className="text-xs text-muted-foreground">Concepto</Label>
                  <Input
                    id="rc-concepto"
                    value={form.concepto}
                    placeholder="Ej. Bodegaje en destino"
                    onChange={(e) => set('concepto', e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Del catálogo (opcional)</Label>
                  <ConceptoCombobox
                    catalogo={catalogo}
                    value={form.conceptoCodigo}
                    onSelect={(c) =>
                      setForm((f) => ({
                        ...f,
                        conceptoCodigo: c?.codigo || null,
                        // Si todavía no escribió el concepto, se usa el del catálogo.
                        concepto: f.concepto.trim() ? f.concepto : c?.nombre || '',
                      }))
                    }
                  />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label htmlFor="rc-prov" className="text-xs text-muted-foreground">Quién lo cobra</Label>
                  <Input
                    id="rc-prov"
                    value={form.proveedorNombre}
                    placeholder="Naviera, coloader, bodega…"
                    onChange={(e) => set('proveedorNombre', e.target.value)}
                  />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label htmlFor="rc-desc" className="text-xs text-muted-foreground">Descripción (opcional)</Label>
                  <Input
                    id="rc-desc"
                    value={form.descripcion}
                    placeholder="Ej. 3 días de bodegaje por demora en el levante"
                    onChange={(e) => set('descripcion', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                {/* Costo */}
                <div className="space-y-1 rounded-md border p-3">
                  <Label htmlFor="rc-costo" className="text-xs text-muted-foreground">
                    Costo del proveedor
                  </Label>
                  <div className="flex gap-2">
                    <select
                      className={cn(selectCls, 'w-24')}
                      value={form.costoMoneda}
                      aria-label="Moneda del costo"
                      onChange={(e) => {
                        const m = e.target.value;
                        // Mientras el cobro esté en la moneda del costo, la sigue.
                        setForm((f) => ({
                          ...f,
                          costoMoneda: m,
                          cobroMoneda: f.cobroMoneda === f.costoMoneda ? m : f.cobroMoneda,
                        }));
                      }}
                    >
                      {MONEDAS.map((m) => <option key={m}>{m}</option>)}
                    </select>
                    <Input
                      id="rc-costo"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.costoMonto}
                      onChange={(e) => set('costoMonto', e.target.value)}
                    />
                  </div>
                </div>

                {/* Cobro */}
                <div className="space-y-1 rounded-md border p-3">
                  <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={form.trasladarCliente}
                      onChange={(e) => set('trasladarCliente', e.target.checked)}
                    />
                    Se le cobra al cliente
                  </label>
                  {form.trasladarCliente ? (
                    <div className="flex gap-2">
                      <select
                        className={cn(selectCls, 'w-24')}
                        value={form.cobroMoneda}
                        aria-label="Moneda del cobro"
                        onChange={(e) => set('cobroMoneda', e.target.value)}
                      >
                        {MONEDAS.map((m) => <option key={m}>{m}</option>)}
                      </select>
                      <Input
                        id="rc-cobro"
                        type="number"
                        min="0"
                        step="0.01"
                        aria-label="Valor a cobrar"
                        value={form.cobroMonto}
                        onChange={(e) => set('cobroMonto', e.target.value)}
                      />
                    </div>
                  ) : (
                    <p className="pt-2 text-xs text-muted-foreground">
                      No se traslada: lo absorbe el operador.
                    </p>
                  )}
                </div>
              </div>

              {!form._id && (
                <div className="space-y-2 rounded-md border border-dashed p-3">
                  <label className="flex cursor-pointer items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.publicar}
                      onChange={(e) => set('publicar', e.target.checked)}
                    />
                    <Route className="h-4 w-4 text-primary" />
                    Registrar en la trazabilidad
                    <span className="text-xs text-muted-foreground">(la ve el cliente)</span>
                  </label>
                  {form.publicar && (
                    <div className="space-y-2 pl-6">
                      <div className="space-y-1">
                        <Label htmlFor="rc-texto-cliente" className="text-xs text-muted-foreground">
                          Texto que verá el cliente
                        </Label>
                        <Textarea
                          id="rc-texto-cliente"
                          rows={2}
                          maxLength={1000}
                          // Mientras no lo edites, sigue al concepto y al cobro.
                          value={form.textoEditado ? form.textoCliente : textoParaCliente(form)}
                          placeholder="Escribe primero el concepto del recargo"
                          onChange={(e) =>
                            setForm((f) => ({ ...f, textoCliente: e.target.value, textoEditado: true }))
                          }
                        />
                        <p className="text-[11px] text-muted-foreground">
                          No incluye el costo del proveedor ni quién lo cobra.
                          {form.textoEditado && (
                            <button
                              type="button"
                              className="ml-2 underline"
                              onClick={() => setForm((f) => ({ ...f, textoEditado: false }))}
                            >
                              Volver al texto automático
                            </button>
                          )}
                        </p>
                      </div>
                      <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                        <input
                          type="checkbox"
                          checked={form.avisarCliente}
                          onChange={(e) => set('avisarCliente', e.target.checked)}
                        />
                        Avisar al cliente por correo
                      </label>
                    </div>
                  )}
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <input
                    ref={inputArchivo}
                    type="file"
                    className="hidden"
                    accept=".pdf,image/*,.doc,.docx,.xls,.xlsx"
                    onChange={(e) => {
                      elegirArchivo(e.target.files?.[0]);
                      e.target.value = '';
                    }}
                  />
                  {archivo || form.soporte ? (
                    <Badge variant="outline" className="gap-1 font-normal">
                      <Paperclip className="h-3 w-3" />
                      {archivo ? archivo.name : form.soporte.nombreOriginal || 'Soporte'}
                      <button
                        type="button"
                        className="ml-1 text-muted-foreground hover:text-destructive"
                        title="Quitar soporte"
                        onClick={() => {
                          setArchivo(null);
                          set('soporte', null);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => inputArchivo.current?.click()}>
                      <Paperclip className="mr-1.5 h-3.5 w-3.5" /> Adjuntar soporte (opcional)
                    </Button>
                  )}
                  {margenForm != null && (
                    <span className="text-xs text-muted-foreground">
                      Utilidad:{' '}
                      <span className={cn('font-medium', margenForm < 0 ? 'text-rose-700' : 'text-emerald-700')}>
                        {importe(margenForm, form.costoMoneda)}
                      </span>
                    </span>
                  )}
                  {form.trasladarCliente && form.cobroMoneda !== form.costoMoneda && (
                    <span className="text-xs text-muted-foreground">
                      Monedas distintas: la utilidad no se calcula.
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={guardando} onClick={() => setForm(null)}>
                    Cancelar
                  </Button>
                  <Button size="sm" disabled={!formValido || guardando} onClick={guardar}>
                    {guardando ? (
                      <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-1.5 h-4 w-4" />
                    )}
                    {form._id ? 'Guardar cambios' : 'Registrar'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Lista */}
          {cargando ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cargando…
            </div>
          ) : datos.recargos.length === 0 ? (
            !form && (
              <div className="flex flex-col items-center rounded-lg border border-dashed py-12 text-center">
                <Receipt className="mb-2 h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Sin recargos en esta operación.</p>
                <Button size="sm" className="mt-3" onClick={nuevo}>
                  <Plus className="mr-1.5 h-4 w-4" /> Registrar recargo
                </Button>
              </div>
            )
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Fecha</th>
                    <th className="px-3 py-2 text-left font-medium">Concepto</th>
                    <th className="px-3 py-2 text-right font-medium">Costo</th>
                    <th className="px-3 py-2 text-right font-medium">Cobro al cliente</th>
                    <th className="px-3 py-2 text-right font-medium">Utilidad</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {datos.recargos.map((r) => (
                    <tr
                      key={r._id}
                      className={cn('border-t align-top', form?._id === r._id && 'bg-primary/5')}
                    >
                      <td className="whitespace-nowrap px-3 py-2">{fecha(r.fecha)}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium">{r.concepto}</div>
                        <div className="text-xs text-muted-foreground">
                          {[r.proveedorNombre, r.descripcion].filter(Boolean).join(' · ')}
                        </div>
                        {r.hitoTrazabilidadId && (
                          <Badge
                            variant="outline"
                            className="mt-1 gap-1 border-blue-200 bg-blue-50 font-normal text-blue-800"
                            title="Publicado como hito en la trazabilidad del cliente"
                          >
                            <Route className="h-3 w-3" /> en trazabilidad
                          </Badge>
                        )}
                        {r.autorNombre && (
                          <div className="text-[11px] text-muted-foreground">por {r.autorNombre}</div>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right">
                        {importe(r.costoMonto, r.costoMoneda)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right">
                        {r.trasladarCliente ? (
                          importe(r.cobroMonto, r.cobroMoneda)
                        ) : (
                          <span className="text-xs text-muted-foreground">no se cobra</span>
                        )}
                      </td>
                      <td
                        className={cn(
                          'whitespace-nowrap px-3 py-2 text-right font-medium',
                          r.margen == null
                            ? 'text-muted-foreground'
                            : r.margen < 0
                              ? 'text-rose-700'
                              : 'text-emerald-700',
                        )}
                        title={r.margen == null ? 'Costo y cobro en monedas distintas' : ''}
                      >
                        {r.margen == null ? '—' : importe(r.margen, r.costoMoneda)}
                      </td>
                      <td className="whitespace-nowrap px-2 py-1.5 text-right">
                        {r.soporte && (
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Ver soporte" onClick={() => verSoporte(r)}>
                            <FileText className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Editar" onClick={() => editar(r)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          title="Eliminar"
                          onClick={() => setABorrar(r)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </DialogContent>

      <AlertDialog open={!!aBorrar} onOpenChange={(v) => !v && setABorrar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar el recargo?</AlertDialogTitle>
            <AlertDialogDescription>
              Se elimina <strong>{aBorrar?.concepto}</strong>
              {aBorrar?.soporte ? ' y su archivo de soporte' : ''}. No se puede deshacer.
              {aBorrar?.hitoTrazabilidadId && (
                <>
                  {' '}
                  El hito que se publicó en la trazabilidad <strong>no se borra</strong>: el
                  cliente ya pudo verlo. Si hace falta, quítalo desde la trazabilidad.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={borrar}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
